import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { Select } from "../ui/Select";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { enrollmentsApi } from "../../api/enrollments";
import { cabinsApi } from "../../api/cabins";
import { cabinGroupsApi } from "../../api/cabinGroups";
import { termsApi } from "../../api/terms";
import type { Enrollment, CabinGroup, Term } from "../../types";

const FULL_TERM = "full_term";

const currentYear = new Date().getFullYear();

const schema = z.object({
  year: z.coerce
    .number()
    .int("Year must be a whole number")
    .min(2000, "Year must be 2000 or later")
    .max(currentYear + 5, `Year must be ${currentYear + 5} or earlier`),
});

type FormData = z.infer<typeof schema>;

interface Props {
  camperId: number;
  /** Provide an existing enrollment to edit; omit for create */
  enrollment?: Enrollment;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function EnrollmentForm({ camperId, enrollment, onSuccess, onCancel }: Props) {
  const [cabinGroups, setCabinGroups] = useState<CabinGroup[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [loadingCabins, setLoadingCabins] = useState(true);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(
    enrollment?.cabin?.cabinGroupId ? String(enrollment.cabin.cabinGroupId) : ""
  );
  const [selectedTermId, setSelectedTermId] = useState<string>(
    enrollment?.termId ? String(enrollment.termId) : ""
  );

  const isEdit = !!enrollment;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      year: enrollment?.year ?? currentYear,
    },
  });

  const watchedYear = watch("year");

  useEffect(() => {
    cabinGroupsApi
      .list()
      .then((all) => setCabinGroups(all.filter((g) => !g.archivedAt)))
      .catch(() => setCabinGroups([]))
      .finally(() => setLoadingCabins(false));
  }, []);

  useEffect(() => {
    const year = Number(watchedYear);
    if (!year || year < 2000) return;
    setLoadingTerms(true);
    termsApi
      .list({ year })
      .then((all) => {
        setTerms(all);
        // Clear selection if it no longer matches (don't clear FULL_TERM)
        if (selectedTermId && selectedTermId !== FULL_TERM && !all.find((t) => t.id === Number(selectedTermId))) {
          setSelectedTermId("");
        }
      })
      .catch(() => setTerms([]))
      .finally(() => setLoadingTerms(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedYear]);

  const resolveCabinId = async (): Promise<number | null> => {
    if (!selectedGroupId) return null;
    const group = cabinGroups.find((g) => g.id === Number(selectedGroupId));
    if (!group) return null;
    if (group.cabins && group.cabins.length > 0) return group.cabins[0].id;
    const newCabin = await cabinsApi.create({ name: group.name, cabinGroupId: group.id });
    return newCabin.id;
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const cabinId = await resolveCabinId();

      if (!isEdit && selectedTermId === FULL_TERM) {
        // Create one enrollment per term for the year
        if (terms.length === 0) {
          toast.error("No terms found for this year to enroll in");
          return;
        }
        const results = await Promise.allSettled(
          terms.map((t) =>
            enrollmentsApi.create({ camperId, year: data.year, cabinId, termId: t.id })
          )
        );
        const succeeded = results.filter((r) => r.status === "fulfilled").length;
        const skipped = results.length - succeeded;
        if (succeeded === 0) {
          toast.error("All term enrollments already exist");
        } else if (skipped > 0) {
          toast.success(`Enrolled in ${succeeded} term${succeeded !== 1 ? "s" : ""} (${skipped} already existed)`);
        } else {
          toast.success(`Enrolled in all ${succeeded} term${succeeded !== 1 ? "s" : ""}`);
        }
        onSuccess();
        return;
      }

      const termId = selectedTermId && selectedTermId !== FULL_TERM ? Number(selectedTermId) : null;
      const payload = { camperId, year: data.year, cabinId, termId };

      if (isEdit) {
        await enrollmentsApi.update(enrollment.id, payload);
        toast.success("Enrollment updated");
      } else {
        await enrollmentsApi.create(payload);
        toast.success("Enrollment added");
      }

      onSuccess();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save enrollment");
    } finally {
      setSubmitting(false);
    }
  };

  const termOptions = [
    ...(!isEdit && terms.length > 0 ? [{ value: FULL_TERM, label: "Full Term" }] : []),
    ...terms.map((t) => ({ value: String(t.id), label: t.name })),
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Year"
        type="number"
        min={2000}
        max={currentYear + 5}
        error={errors.year?.message}
        {...register("year")}
      />

      <Select
        label="Term (optional)"
        options={termOptions}
        placeholder={loadingTerms ? "Loading terms…" : "No term assigned"}
        value={selectedTermId}
        onChange={(e) => setSelectedTermId(e.target.value)}
      />

      <Select
        label="Cabin (optional)"
        options={cabinGroups.map((g) => ({ value: g.id, label: g.name }))}
        placeholder={loadingCabins ? "Loading cabins…" : "No cabin assigned"}
        value={selectedGroupId}
        onChange={(e) => setSelectedGroupId(e.target.value)}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={submitting}>
          {isEdit ? "Save Changes" : "Add Enrollment"}
        </Button>
      </div>
    </form>
  );
}
